import React, { memo, useEffect, useState } from "react";
import { AlertTriangle, Trash2, Info } from "lucide-react";
import { useTranslations } from "use-intl";
import { Modal, ModalBody, ModalFooter } from "./Modal";
import Button from "../button/Button";
import { cn } from "@/utils";

export type ConfirmationVariant = "danger" | "warning" | "info";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationVariant;
  /**
   * For extreme destructive actions (delete org/account). User must type
   * this exact string to enable the confirm button. Trim+case-sensitive.
   */
  requireType?: string;
}

const variantStyles: Record<
  ConfirmationVariant,
  { bg: string; icon: string; buttonVariant: "destructive" | "primary" }
> = {
  danger: {
    bg: "bg-[var(--destructive-bg)]",
    icon: "text-[var(--destructive)]",
    buttonVariant: "destructive",
  },
  warning: {
    bg: "bg-[var(--warning-bg)]",
    icon: "text-[var(--warning)]",
    buttonVariant: "primary",
  },
  info: {
    bg: "bg-[var(--info-bg)]",
    icon: "text-[var(--info)]",
    buttonVariant: "primary",
  },
};

const icons: Record<ConfirmationVariant, React.ReactNode> = {
  danger: <Trash2 className="size-6" strokeWidth={2} />,
  warning: <AlertTriangle className="size-6" strokeWidth={2} />,
  info: <Info className="size-6" strokeWidth={2} />,
};

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  title,
  message,
  confirmText,
  cancelText,
  variant = "danger",
  requireType,
}: ConfirmationModalProps) {
  const t = useTranslations("common");
  // Fall back to translated defaults so callers don't need to pass every string
  const resolvedConfirmText = confirmText ?? t("confirm");
  const resolvedCancelText = cancelText ?? t("cancel");
  const styles = variantStyles[variant];
  const icon = icons[variant];
  const [typed, setTyped] = useState("");

  // Reset typed state on open/close transitions so a re-opened dialog
  // never inherits stale input from an earlier session.
  useEffect(() => {
    if (!isOpen) setTyped("");
  }, [isOpen]);

  const typeMatched = !requireType || typed.trim() === requireType;
  const confirmDisabled = isLoading || !typeMatched;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" aria-label={title}>
      <ModalBody className="p-6">
        <div className="flex flex-col items-center text-center">
          <div
            className={cn(
              "mb-4 flex size-14 items-center justify-center rounded-full",
              styles.bg
            )}
          >
            <span className={styles.icon}>{icon}</span>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-[var(--foreground)]">
            {title}
          </h3>
          <div className="text-sm text-[var(--muted-foreground)]">
            {message}
          </div>
          {requireType && (
            <div className="mt-5 w-full text-left">
              {/* Render the translated prefix + monospace value inline — rich-text
                  interpolation is not used here to keep the value styled separately. */}
              <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1.5">
                {t("require_type_label", { value: requireType }).split(requireType)[0]}
                <span className="font-mono text-[var(--foreground)]">{requireType}</span>
                {t("require_type_label", { value: requireType }).split(requireType)[1]}
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={isLoading}
                autoComplete="off"
                spellCheck={false}
                className={cn(
                  "w-full h-9 px-3 text-sm font-mono rounded-[8px] bg-[var(--card)]",
                  "border border-[var(--border)] text-[var(--foreground)]",
                  "focus:outline-hidden focus:border-[var(--ring)] focus:shadow-[var(--shadow-focus-ring)]",
                  "disabled:opacity-60"
                )}
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="flex justify-end gap-3 border-t border-[var(--border)] p-4">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          {resolvedCancelText}
        </Button>
        <Button
          variant={styles.buttonVariant}
          onClick={onConfirm}
          disabled={confirmDisabled}
        >
          {isLoading ? t("processing") : resolvedConfirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default memo(ConfirmationModal);
